import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

// project import
import useAuth from 'hooks/useAuth';

// types
import { GuardProps } from 'types/auth';
import Loader from '../../components/Loader';
import { config } from '../../env';
import { ROUTES_TOUR } from '../../sections/welcome-tour/tourRoutes';
import { resolveWelcomeTourVariant } from '../experiment';
const ENV_NAME = config.env_name;

// ==============================|| AUTH GUARD ||============================== //

const AuthGuard = ({ children }: GuardProps) => {
  const { isAuthenticated, isSignUpFinished, user } = useAuth();
  const navigate = useNavigate();
  const [isAdmin, setIsAdmin] = useState<boolean | undefined>(undefined);
  const [isPaid, setIsPaid] = useState<boolean | undefined>(undefined);
  // The effect re-runs on every dep change while the async flag lookup is in
  // flight — resolve the experiment only once per mount.
  const enrollStarted = useRef(false);

  useEffect(() => {
    const localMaintenance = localStorage.getItem('CURRENT_MAINTENANCE_' + ENV_NAME);
    if (localMaintenance && localMaintenance.toUpperCase() === 'TRUE') {
      navigate('maintenance', { replace: true });
    }
    setIsAdmin(user?.roles?.includes('Admin'));
    setIsPaid(user?.roles?.includes('Paid Member'));
    if (!isAuthenticated) {
      navigate('login', { replace: true });
    } else if (isSignUpFinished !== undefined && isAuthenticated && !isSignUpFinished) {
      navigate('welcome', { replace: false });
      // Exact match — a prefix test also caught siblings like /welcome-tour.
      // Straight off signup: the welcome-tour experiment decides what a new
      // user sees first. test → guided tour, control (or PostHog unreachable/
      // flag off, the kill switch) → dashboard.
    } else if (isSignUpFinished && isAuthenticated && window.location.pathname === '/welcome') {
      if (!enrollStarted.current) {
        enrollStarted.current = true;
        resolveWelcomeTourVariant().then((variant) => {
          // The dashboard is the single non-tour destination — the get-started
          // experiment is applied THERE (test arm mounts it locked), not by
          // routing, so exposure has one home.
          // ?from=signup: this user just came through the landing page and
          // the signup form — they have READ the pitch. The tour uses the flag
          // to skip its welcome re-pitch and land them on the first decision.
          navigate(variant === 'test' ? `${ROUTES_TOUR.TOUR}?from=signup` : ROUTES_TOUR.DASHBOARD, { replace: true });
        });
      }
    } else if (
      isSignUpFinished &&
      isAuthenticated &&
      (isAdmin === true || isPaid === true) &&
      window.location.pathname.includes('pricing')
    ) {
      navigate('dashboard', { replace: true });
    }
  }, [isAuthenticated, navigate, isSignUpFinished, user, isPaid, isAdmin]);

  //if (isSignUpFinished !== undefined && (isAdmin || isPaid || window.location.pathname.slice(0, 8) === '/welcome')) {
  if (isSignUpFinished !== undefined) {
    return children;
  } else {
    return <Loader />;
  }
};

export default AuthGuard;
